import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { POST } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import CommentThread from "@/components/CommentThread";
import PostMedia from "@/components/PostMedia";
import FinancierPanel from "@/components/FinancierPanel";

const EMOTION_LABEL = {
  melancholy: "Melancholy",
  rage: "Rage",
  serenity: "Serenity",
  anxiety: "Anxiety",
  euphoria: "Euphoria",
};

function ConsistencyGrid({ consistency }) {
  const cells = Math.max(4, Math.round((consistency / 100) * 24));
  const cols = Math.min(cells, 24);
  return (
    <div
      data-testid={POST.consistencyGrid}
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          `linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px),` +
          `linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)`,
        backgroundSize: `${100 / cols}% ${100 / cols}%`,
        opacity: 0.4 + (100 - consistency) / 200,
      }}
    />
  );
}

function PostHeader({ post }) {
  return (
    <header className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-[#1a1d28] flex items-center justify-center text-xs font-mono text-yellow-400 border border-[#2a2f3f] shrink-0">
          {post.author_name?.[0]?.toUpperCase() || "·"}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-100 truncate">{post.author_name}</p>
          <p className="text-[10px] uppercase font-mono tracking-widest text-slate-500">
            {new Date(post.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      <Badge
        data-testid={POST.emotionBadge}
        className="font-mono text-[10px] uppercase tracking-widest bg-black/40 text-slate-200 border border-white/10"
      >
        {EMOTION_LABEL[post.emotion] || post.emotion}
      </Badge>
    </header>
  );
}

function PostMeter({ label, value, color, testId }) {
  return (
    <div className="mt-1 flex items-center gap-3 text-[10px] font-mono text-slate-400 uppercase tracking-widest">
      <span>{label}</span>
      <div data-testid={testId} className="flex-1 h-1 bg-white/5 rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${value}%` }} />
      </div>
      <span>{value}%</span>
    </div>
  );
}

export default function PostCard({ post, onUpdate }) {
  const { user } = useAuth();
  const [openFinancier, setOpenFinancier] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(null);
  const totalVotes = post.votes_for + post.votes_against;
  const bounced = post.financing_bounced || (post.votes_against >= post.votes_for && totalVotes > 0);
  const intensityStyle = useMemo(
    () => ({
      "--em-light": 0.08 + post.intensity / 300,
      "--em-fonce": 0.4 + post.intensity / 170,
    }),
    [post.intensity]
  );

  const vote = async (direction) => {
    if (!user) return toast.error("Sign in first");
    if (!user.is_member) return toast.error("Membership required to vote");
    try {
      await api.post(`/posts/${post.post_id}/vote`, { direction });
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    }
  };

  return (
    <article
      data-testid={POST.card}
      className={`relative overflow-hidden rounded telesis-rise emotion-${post.emotion}`}
      style={intensityStyle}
    >
      <ConsistencyGrid consistency={post.consistency} />
      <div className="relative z-10 p-5 md:p-6 backdrop-blur-[2px]">
        <PostHeader post={post} />

        <p className="text-sm md:text-base text-slate-100 leading-relaxed whitespace-pre-wrap">{post.content}</p>

        <PostMedia mediaPath={post.media_path} consistency={post.consistency} />

        <div className="mt-4">
          <PostMeter label="Intensity" value={post.intensity} color="bg-yellow-400" testId={POST.intensityBar} />
          <PostMeter label="Consistency" value={post.consistency} color="bg-emerald-400" />
        </div>

        {bounced && (
          <div
            data-testid={POST.financingBounceAlert}
            className="mt-3 px-3 py-2 bg-rose-500/10 border border-rose-500/40 rounded text-xs font-mono uppercase tracking-widest text-rose-300"
          >
            Financing bounced · Against ≥ For
          </div>
        )}

        <footer className="mt-4 flex flex-wrap items-center gap-2">
          <button
            data-testid={POST.voteFor}
            onClick={() => vote("for")}
            className="telesis-pill bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
          >
            For · {post.votes_for}
          </button>
          <button
            data-testid={POST.voteAgainst}
            onClick={() => vote("against")}
            className="telesis-pill bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
          >
            Against · {post.votes_against}
          </button>
          <button
            data-testid={POST.financierPledge}
            onClick={() => setOpenFinancier((v) => !v)}
            disabled={bounced}
            className="telesis-pill bg-yellow-400/10 text-yellow-300 hover:bg-yellow-400/20 disabled:opacity-30"
          >
            Finance · ${(post.financier_total_cents / 100).toFixed(2)}
          </button>
          <button
            data-testid={POST.commentsToggle}
            onClick={() => setShowComments((v) => !v)}
            className="telesis-pill bg-white/5 text-slate-300 hover:bg-white/10"
          >
            {showComments ? "Hide" : "Comments"}
            {commentCount !== null ? ` · ${commentCount}` : ""}
          </button>
        </footer>

        {openFinancier && !bounced && (
          <FinancierPanel
            postId={post.post_id}
            onDone={() => {
              setOpenFinancier(false);
              onUpdate?.();
            }}
          />
        )}

        {showComments && (
          <CommentThread postId={post.post_id} onCountChange={setCommentCount} />
        )}
      </div>
    </article>
  );
}
